/**
 * RulesTable component - displays processing rules grouped by agent type.
 */

import { AgentTypeInfo } from "@/components/rules/AgentTypeInfo";
import { ScopeBadges } from "@/components/rules/ScopeBadges";
import { useUpdateRule } from "@/hooks/useRules";
import { AGENT_TYPE_LABELS, AgentType, ProcessingRule } from "@/types/rules";
import { formatAbsoluteTime, formatRelativeTime } from "@/utils/dateUtils";
import { CircularProgress, Switch, Tooltip } from "@mui/material";
import { useSnackbar } from "notistack";
import { useCallback, useMemo, useState } from "react";

interface RulesTableProps {
  rules: ProcessingRule[];
  isLoading: boolean;
  onEdit: (rule: ProcessingRule) => void;
  onDelete: (rule: ProcessingRule) => void;
}

/**
 * Group rules by agent_type and sort by priority within each group
 */
function groupRulesByAgent(rules: ProcessingRule[]): Record<AgentType, ProcessingRule[]> {
  const grouped: Record<AgentType, ProcessingRule[]> = {
    classifier: [],
    extractor: [],
    matcher: [],
    validator: [],
  };

  rules.forEach((rule) => {
    const agentType = rule.agent_type as AgentType;
    if (grouped[agentType]) {
      grouped[agentType].push(rule);
    }
  });

  // Sort each group by priority (ascending)
  Object.keys(grouped).forEach((key) => {
    const agentType = key as AgentType;
    grouped[agentType].sort((a, b) => a.rule_priority - b.rule_priority);
  });

  return grouped;
}

export function RulesTable({ rules, isLoading, onEdit, onDelete }: RulesTableProps) {
  const { enqueueSnackbar } = useSnackbar();
  const updateRuleMutation = useUpdateRule();
  const [editingPriorityId, setEditingPriorityId] = useState<number | null>(null);
  const [priorityValue, setPriorityValue] = useState<string>("");
  const [expandedRules, setExpandedRules] = useState<Set<number>>(new Set());
  const [collapsedSections, setCollapsedSections] = useState<Set<AgentType>>(new Set());
  const [savingPriorityId, setSavingPriorityId] = useState<number | null>(null);

  // Memoize grouped rules to avoid recalculation on every render
  const groupedRules = useMemo(() => groupRulesByAgent(rules), [rules]);

  const toggleSection = useCallback((agentType: AgentType) => {
    setCollapsedSections((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(agentType)) {
        newSet.delete(agentType);
      } else {
        newSet.add(agentType);
      }
      return newSet;
    });
  }, []);

  const handleToggleActive = useCallback(
    async (rule: ProcessingRule) => {
      try {
        await updateRuleMutation.mutateAsync({
          ruleId: rule.id,
          data: { is_active: !rule.is_active },
        });
        enqueueSnackbar(`Rule ${rule.is_active ? "deactivated" : "activated"} successfully`, {
          variant: "success",
        });
      } catch (error) {
        const message = error instanceof Error ? error.message : "Unknown error";
        enqueueSnackbar(`Failed to update rule: ${message}`, { variant: "error" });
      }
    },
    [updateRuleMutation, enqueueSnackbar]
  );

  const handlePriorityEdit = useCallback((rule: ProcessingRule) => {
    setEditingPriorityId(rule.id);
    setPriorityValue(String(rule.rule_priority));
  }, []);

  const handlePrioritySave = useCallback(
    async (ruleId: number) => {
      const newPriority = parseInt(priorityValue, 10);

      // Comprehensive validation
      if (isNaN(newPriority)) {
        enqueueSnackbar("Priority must be a valid number", { variant: "error" });
        setEditingPriorityId(null);
        return;
      }

      if (!Number.isInteger(newPriority)) {
        enqueueSnackbar("Priority must be a whole number", { variant: "error" });
        setEditingPriorityId(null);
        return;
      }

      if (newPriority <= 0) {
        enqueueSnackbar("Priority must be greater than 0", { variant: "error" });
        setEditingPriorityId(null);
        return;
      }

      if (newPriority > 10000) {
        enqueueSnackbar("Priority cannot exceed 10000", { variant: "error" });
        setEditingPriorityId(null);
        return;
      }

      setSavingPriorityId(ruleId);

      try {
        await updateRuleMutation.mutateAsync({
          ruleId,
          data: { rule_priority: newPriority },
        });
        enqueueSnackbar("Priority updated successfully", { variant: "success" });
        setEditingPriorityId(null);
      } catch (error) {
        const message = error instanceof Error ? error.message : "Unknown error";
        enqueueSnackbar(`Failed to update priority: ${message}`, { variant: "error" });
        setEditingPriorityId(null);
      } finally {
        setSavingPriorityId(null);
      }
    },
    [priorityValue, updateRuleMutation, enqueueSnackbar]
  );

  const handlePriorityCancel = useCallback(() => {
    setEditingPriorityId(null);
    setPriorityValue("");
  }, []);

  const handlePriorityKeyDown = useCallback(
    (e: React.KeyboardEvent, ruleId: number) => {
      if (e.key === "Enter") {
        e.preventDefault();
        handlePrioritySave(ruleId);
      } else if (e.key === "Escape") {
        e.preventDefault();
        handlePriorityCancel();
      }
    },
    [handlePrioritySave, handlePriorityCancel]
  );

  const toggleExpanded = useCallback((ruleId: number) => {
    setExpandedRules((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(ruleId)) {
        newSet.delete(ruleId);
      } else {
        newSet.add(ruleId);
      }
      return newSet;
    });
  }, []);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <CircularProgress />
      </div>
    );
  }

  if (rules.length === 0) {
    return (
      <div className="rounded-xl border border-gray-200 bg-white p-8 text-center dark:border-[#232f48] dark:bg-[#192233]">
        <span className="material-symbols-outlined mb-2 text-4xl text-slate-400">rule</span>
        <p className="text-slate-600 dark:text-slate-400">No rules found</p>
      </div>
    );
  }

  return (
    <div className="space-y-12">
      {(Object.keys(groupedRules) as AgentType[]).map((agentType) => {
        const agentRules = groupedRules[agentType];
        if (agentRules.length === 0) return null;

        const isCollapsed = collapsedSections.has(agentType);

        return (
          <section key={agentType}>
            <div className="mb-4 flex items-center gap-3">
              <button
                onClick={() => toggleSection(agentType)}
                className="rounded p-1 text-slate-400 transition-all hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-800 dark:hover:text-slate-200"
                aria-label={`${isCollapsed ? "Expand" : "Collapse"} ${AGENT_TYPE_LABELS[agentType]} section`}
                aria-expanded={!isCollapsed}
              >
                <span
                  className={`material-symbols-outlined text-xl transition-transform duration-200 ${
                    isCollapsed ? "-rotate-90" : ""
                  }`}
                >
                  expand_more
                </span>
              </button>
              <span
                className={`rounded p-1 ${
                  agentType === "classifier"
                    ? "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400"
                    : agentType === "extractor"
                      ? "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400"
                      : agentType === "matcher"
                        ? "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400"
                        : "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"
                }`}
              >
                <span className="material-symbols-outlined text-lg leading-none">
                  {agentType === "classifier"
                    ? "category"
                    : agentType === "extractor"
                      ? "data_object"
                      : agentType === "matcher"
                        ? "link"
                        : "check_circle"}
                </span>
              </span>
              <h3 className="text-lg font-bold text-slate-800 dark:text-slate-200">
                {AGENT_TYPE_LABELS[agentType]}{" "}
                <span className="ml-2 text-sm font-normal text-slate-400">
                  ({agentRules.length} {agentRules.length === 1 ? "Rule" : "Rules"})
                </span>
              </h3>
              <AgentTypeInfo agentType={agentType} />
            </div>

            {!isCollapsed && (
              <div className="space-y-3">
                {agentRules.map((rule) => {
                  const isExpanded = expandedRules.has(rule.id);
                  const truncatedText =
                    rule.rule_text.length > 150
                      ? rule.rule_text.substring(0, 150) + "..."
                      : rule.rule_text;

                  return (
                    <div
                      key={rule.id}
                      className={`flex flex-col gap-6 rounded-xl border bg-white p-4 transition-all dark:bg-[#192233] md:flex-row md:items-center ${
                        rule.is_active
                          ? "border-gray-200 dark:border-[#232f48]"
                          : "border-dashed border-gray-300 opacity-80 dark:border-[#2a3f5f]"
                      }`}
                    >
                      {/* Left: ID and Rule Text */}
                      <div className="min-w-0 flex-1">
                        <div className="mb-2 flex items-center gap-2">
                          <span className="text-xs font-bold text-slate-400">#{rule.id}</span>
                          <ScopeBadges rule={rule} />
                        </div>
                        <p className="text-sm text-slate-700 dark:text-slate-300">
                          {isExpanded ? rule.rule_text : truncatedText}
                          {rule.rule_text.length > 150 && (
                            <button
                              onClick={() => toggleExpanded(rule.id)}
                              className="ml-2 text-xs font-medium text-primary hover:underline"
                              aria-label={isExpanded ? "Collapse rule text" : "Expand rule text"}
                              aria-expanded={isExpanded}
                            >
                              {isExpanded ? "Show less" : "Show more"}
                            </button>
                          )}
                        </p>
                        <div className="mt-2 flex items-center gap-4 text-xs text-slate-500 dark:text-slate-400">
                          <Tooltip title={formatAbsoluteTime(rule.created_at)} arrow>
                            <span>Created {formatRelativeTime(rule.created_at)}</span>
                          </Tooltip>
                          {rule.updated_at && (
                            <Tooltip title={formatAbsoluteTime(rule.updated_at)} arrow>
                              <span>Updated {formatRelativeTime(rule.updated_at)}</span>
                            </Tooltip>
                          )}
                        </div>
                      </div>

                      {/* Right: Priority, Active Toggle, Actions */}
                      <div className="flex flex-shrink-0 items-center gap-4">
                        {/* Priority */}
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-medium text-slate-600 dark:text-slate-400">
                            Priority:
                          </span>
                          {editingPriorityId === rule.id ? (
                            <div className="flex items-center gap-2">
                              <input
                                type="number"
                                value={priorityValue}
                                onChange={(e) => setPriorityValue(e.target.value)}
                                onBlur={() => handlePrioritySave(rule.id)}
                                onKeyDown={(e) => handlePriorityKeyDown(e, rule.id)}
                                className="w-16 rounded border border-primary bg-slate-50 px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-primary dark:bg-[#111722] dark:text-slate-200"
                                autoFocus
                                disabled={savingPriorityId === rule.id}
                                min="1"
                                max="10000"
                                step="1"
                              />
                              {savingPriorityId === rule.id && (
                                <CircularProgress size={14} className="text-primary" />
                              )}
                            </div>
                          ) : (
                            <button
                              onClick={() => handlePriorityEdit(rule)}
                              onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                  e.preventDefault();
                                  handlePriorityEdit(rule);
                                }
                              }}
                              className="rounded bg-slate-100 px-2 py-1 text-sm font-bold text-slate-700 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"
                              aria-label={`Edit priority for rule ${rule.id}`}
                              title="Click to edit priority"
                              disabled={savingPriorityId !== null}
                            >
                              {rule.rule_priority}
                            </button>
                          )}
                        </div>

                        {/* Active Toggle */}
                        <div className="flex items-center">
                          <Switch
                            checked={rule.is_active}
                            onChange={() => handleToggleActive(rule)}
                            size="small"
                            disabled={updateRuleMutation.isPending}
                            inputProps={{
                              "aria-label": `${rule.is_active ? "Deactivate" : "Activate"} rule ${rule.id}`,
                            }}
                          />
                        </div>

                        {/* Actions */}
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => onEdit(rule)}
                            className="rounded p-2 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-800 dark:hover:text-slate-200"
                            title="Edit rule"
                          >
                            <span className="material-symbols-outlined text-base">edit</span>
                          </button>
                          <button
                            onClick={() => onDelete(rule)}
                            className="rounded p-2 text-slate-400 transition-colors hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/20 dark:hover:text-red-400"
                            title="Delete rule"
                          >
                            <span className="material-symbols-outlined text-base">delete</span>
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </section>
        );
      })}
    </div>
  );
}
